import { Anchor, Checkbox, Group, Select, Textarea, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { Link } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import type { FieldDescriptor, FieldRegistry, LayoutDocument } from './types';
import type { EntryInputSchema } from '../schemas';

/**
 * Entry-form field registry. Mirror of api/src/lib/entry-form-layout-schema.ts.
 *
 * Each descriptor's `render` function returns the actual Mantine input
 * given the form's control / register and a context bag (for entry: the
 * doc-types and prospects lists).
 */

export const ENTRY_FORM_FIELD_NAMES = [
  'document_type_id',
  'prospect_id',
  'instrument_number',
  'instrument_date',
  'recording_date',
  'book',
  'page',
  'township',
  'range',
  'section',
  'block',
  'abstract',
  'survey',
  'legal_description',
  'consideration',
  'interest_conveyed',
  'depth_limitation',
  'area_limitation',
  'has_mineral_reservation',
  'mineral_reservation_description',
  'reservations',
  'remarks',
  'reviewer_notes',
] as const;

export type EntryFormFieldName = (typeof ENTRY_FORM_FIELD_NAMES)[number];

export interface EntryFormContext {
  docTypes: { id: string; code: string; name: string }[];
  prospects: { id: string; name: string }[];
}

type EntryFieldDescriptor = FieldDescriptor<EntryInputSchema, EntryFormContext>;

function textProps(descriptor: { label: string; placeholder?: string }, disabled: boolean) {
  const props: { label: string; placeholder?: string; disabled?: boolean } = {
    label: descriptor.label,
  };
  if (descriptor.placeholder !== undefined) props.placeholder = descriptor.placeholder;
  if (disabled) props.disabled = true;
  return props;
}

function textField(label: string, defaultSpan: number, opts: { placeholder?: string } = {}): EntryFieldDescriptor {
  const desc: EntryFieldDescriptor = {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <TextInput {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
  if (opts.placeholder !== undefined) desc.placeholder = opts.placeholder;
  return desc;
}

function textareaField(label: string, defaultSpan: number, opts: { placeholder?: string } = {}): EntryFieldDescriptor {
  const desc: EntryFieldDescriptor = {
    label,
    defaultSpan,
    render: ({ name, register, descriptor, disabled }) => (
      <Textarea autosize minRows={2} {...textProps(descriptor, disabled)} {...register(name)} />
    ),
  };
  if (opts.placeholder !== undefined) desc.placeholder = opts.placeholder;
  return desc;
}

function dateField(label: string, defaultSpan: number): EntryFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <DateInput
            label={descriptor.label}
            valueFormat="YYYY-MM-DD"
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v ?? null)}
            clearable
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

function checkboxField(label: string, defaultSpan: number): EntryFieldDescriptor {
  return {
    label,
    defaultSpan,
    render: ({ name, control, descriptor, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Checkbox
            label={descriptor.label}
            checked={(field.value as boolean | null) ?? false}
            onChange={(e) => field.onChange(e.currentTarget.checked)}
            disabled={disabled}
          />
        )}
      />
    ),
  };
}

export const ENTRY_FORM_FIELDS: FieldRegistry<EntryInputSchema, EntryFormFieldName, EntryFormContext> = {
  document_type_id: {
    label: 'Document type',
    defaultSpan: 6,
    render: ({ name, control, descriptor, context, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            label={descriptor.label}
            searchable
            clearable
            data={context.docTypes.map((t) => ({ value: t.id, label: `${t.code} · ${t.name}` }))}
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v ?? null)}
            disabled={disabled}
          />
        )}
      />
    ),
  },
  prospect_id: {
    label: 'Prospect',
    defaultSpan: 6,
    render: ({ name, control, context, disabled }) => (
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            label={
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <span>Prospect</span>
                <Anchor size="xs" component={Link} to="/prospects">
                  Manage
                </Anchor>
              </Group>
            }
            searchable
            clearable
            data={context.prospects.map((p) => ({ value: p.id, label: p.name }))}
            value={(field.value as string | null) ?? null}
            onChange={(v) => field.onChange(v ?? null)}
            disabled={disabled}
          />
        )}
      />
    ),
  },
  instrument_number: textField('Instrument number', 6, { placeholder: 'e.g. 2026-001234' }),
  instrument_date: dateField('Instrument date', 6),
  recording_date: dateField('Recording date', 6),
  book: textField('Book', 6),
  page: textField('Page', 6),
  township: textField('Township', 4),
  range: textField('Range', 4),
  section: textField('Section', 4),
  block: textField('Block', 4),
  abstract: textField('Abstract', 4),
  survey: textField('Survey', 4),
  legal_description: textareaField('Legal description', 12),
  consideration: textField('Consideration', 6),
  interest_conveyed: textField('Interest conveyed', 6),
  depth_limitation: textField('Depth limitation', 6),
  area_limitation: textField('Area limitation', 6),
  has_mineral_reservation: checkboxField('Mineral reservation present', 12),
  mineral_reservation_description: {
    ...textareaField('Mineral reservation description', 12, {
      placeholder: 'Describe the carved-out / reserved mineral interest',
    }),
    visibleWhen: (v) => v.has_mineral_reservation === true,
  },
  reservations: textareaField('Reservations', 12),
  remarks: textareaField('Remarks', 12),
  reviewer_notes: textareaField('Reviewer notes', 12),
};

/** Built-in default. Mirrors api/src/lib/entry-form-layout-schema.ts. */
export const ENTRY_DEFAULT_LAYOUT: LayoutDocument<EntryFormFieldName> = {
  version: 1,
  groups: [
    {
      id: 'basic',
      title: 'Basic',
      fields: [
        { name: 'document_type_id', span: 6 },
        { name: 'prospect_id', span: 6 },
        { name: 'instrument_number', span: 6 },
        { name: 'instrument_date', span: 6 },
        { name: 'recording_date', span: 6 },
        { name: 'book', span: 6 },
        { name: 'page', span: 6 },
      ],
    },
    {
      id: 'legal_anchor',
      title: 'Legal description anchor (T/R/S · Abstract/Survey)',
      fields: [
        { name: 'township', span: 4 },
        { name: 'range', span: 4 },
        { name: 'section', span: 4 },
        { name: 'block', span: 4 },
        { name: 'abstract', span: 4 },
        { name: 'survey', span: 4 },
        { name: 'legal_description', span: 12 },
      ],
    },
    {
      id: 'transaction',
      title: 'Transaction terms',
      fields: [
        { name: 'consideration', span: 6 },
        { name: 'interest_conveyed', span: 6 },
        { name: 'depth_limitation', span: 6 },
        { name: 'area_limitation', span: 6 },
      ],
    },
    {
      id: 'mineral',
      title: 'Mineral reservation',
      fields: [
        { name: 'has_mineral_reservation', span: 12 },
        { name: 'mineral_reservation_description', span: 12 },
      ],
    },
    {
      id: 'notes',
      title: 'Notes',
      fields: [
        { name: 'reservations', span: 12 },
        { name: 'remarks', span: 12 },
        { name: 'reviewer_notes', span: 12 },
      ],
    },
  ],
};
